import debounce from 'lodash.debounce';
import Promise from 'bluebird';
import localStore from 'local-storage';

import refine, { classificationCountsNeedUpdating, encodeLastSearchCookie } from '@seek/seek-refine-job-search';
import { normaliseSearchQuery } from '@seek/seek-jobs-seo';
import createApiClient from 'seek-jobs-api-client';
import { composeReducers } from 'redux-compose-reducers';
const api = createApiClient();

export const UPDATE_CRITERIA = 'UPDATE_CRITERIA';
export const GET_COUNTS_BEGIN = 'GET_COUNTS_BEGIN';
export const GET_COUNTS_SUCCESS = 'GET_COUNTS_SUCCESS';
const GET_COUNTS_ERROR = 'GET_COUNTS_ERROR';
const UPDATE_KEYWORDS_FIELD = 'UPDATE_KEYWORDS_FIELD';
const UPDATE_WHERE_FIELD = 'UPDATE_WHERE_FIELD';
export const UPDATE_ACTIVE_REFINEMENT = 'UPDATE_ACTIVE_REFINEMENT';
const SELECT_REFINEMENT = 'SELECT_REFINEMENT';
const SELECT_CLASSIFICATION = 'SELECT_CLASSIFICATION';
const SELECT_WHERE = 'SELECT_WHERE';
const SUBMIT_SEARCH = 'SUBMIT_SEARCH';
const UPDATE_WHERE_SUGGESTIONS = 'UPDATE_WHERE_SUGGESTIONS';
const GET_PERSONALISED_LOCATION_SUGGESTIONS_SUCCESS = 'GET_PERSONALISED_LOCATION_SUGGESTIONS_SUCCESS';
export const WHERE_FIELD_FOCUSED = 'WHERE_FIELD_FOCUSED';
export const WHERE_FIELD_TYPED = 'WHERE_FIELD_TYPED';
export const WHERE_FIELD_BLANKED = 'WHERE_FIELD_BLANKED';
export const WHERE_FIELD_RECENT_SHOWN = 'WHERE_FIELD_RECENT_SHOWN';
export const WHERE_FIELD_RECENT_SELECTED = 'WHERE_FIELD_RECENT_SELECTED';
export const WHERE_FIELD_SUGGESTED_SHOWN = 'WHERE_FIELD_SUGGESTED_SHOWN';
export const WHERE_FIELD_SUGGESTED_SELECTED = 'WHERE_FIELD_SUGGESTED_SELECTED';
export const WHERE_FIELD_AUTOSUGGESTED_SELECTED = 'WHERE_FIELD_AUTOSUGGESTED_SELECTED';
const SEARCH_HELP_CLICKED = 'SEARCH_HELP_CLICKED';
export const QUICK_SEARCH_CLASSIFICATION_CLICKED = 'QUICK_SEARCH_CLASSIFICATION_CLICKED';
export const QUICK_SEARCH_CITY_CLICKED = 'QUICK_SEARCH_CITY_CLICKED';
export const QUICK_SEARCH_GRADUATE_CLICKED = 'QUICK_SEARCH_GRADUATE_CLICKED';
export const DID_YOU_MEAN_CLICKED = 'DID_YOU_MEAN_CLICKED';
export const SORT_MODE_CHANGED = 'SORT_MODE_CHANGED';
export const JOB_CLASSIFICATION_CLICKED = 'JOB_CLASSIFICATION_CLICKED';
export const JOB_SUBCLASSIFICATION_CLICKED = 'JOB_SUBCLASSIFICATION_CLICKED';
export const JOB_LOCATION_CLICKED = 'JOB_LOCATION_CLICKED';
export const JOB_AREA_CLICKED = 'JOB_AREA_CLICKED';
export const JOB_SUBURB_CLICKED = 'JOB_SUBURB_CLICKED';

import optimisticRefinementReducer from './optimistic/refinement';

const initialState = {
  keywordsField: '',
  whereField: '',
  query: {},
  lastQuery: {},
  hasLoadedCounts: false,
  refinements: null,
  activeRefinement: null,
  whereSuggestions: []
};

const whereSuggestionsCache = { '': [] };
const personalisedSuggestionsCache = {};

const updateRefinements = ({ state, query, counts, locale }) => {
  const { query: currentQuery, refinements: currentRefinements } = state;
  const currentCounts = currentRefinements ? currentRefinements.counts : null;

  const newQuery = query || currentQuery;
  const newRefinements = refine(newQuery, counts || currentCounts, currentQuery, currentCounts, locale || 'AU');

  return {
    query: newQuery,
    refinements: newRefinements
  };
};

function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case UPDATE_CRITERIA: {
      const { lastQuery, query, locale } = action.payload;

      return {
        ...state,
        ...updateRefinements({ state, query, locale }),
        keywordsField: query.keywords || '',
        whereField: query.where || '',
        lastQuery
      };
    }

    case GET_COUNTS_BEGIN: {
      const { query, locale } = action.payload;

      return {
        ...state,
        ...updateRefinements({ state, query, locale })
      };
    }

    case GET_COUNTS_SUCCESS: {
      const { result, locale } = action.payload;

      const counts = {};
      result.counts.forEach(facet => {
        counts[facet.name] = facet.items;
      });

      return {
        ...state,
        ...updateRefinements({ state, counts, locale }),
        hasLoadedCounts: true
      };
    }

    case GET_COUNTS_ERROR: {
      const { locale } = action.payload;
      const counts = {};

      return {
        ...state,
        ...updateRefinements({ state, counts, locale })
      };
    }

    case UPDATE_KEYWORDS_FIELD: {
      const { keywordsField } = action.payload;

      return {
        ...state,
        keywordsField
      };
    }

    case UPDATE_WHERE_FIELD: {
      const { whereField } = action.payload;

      return {
        ...state,
        whereField
      };
    }

    case UPDATE_ACTIVE_REFINEMENT: {
      const { refinement } = action.payload;

      return {
        ...state,
        activeRefinement: refinement
      };
    }

    case UPDATE_WHERE_SUGGESTIONS: {
      const { whereSuggestions } = action.payload;

      return {
        ...state,
        whereSuggestions
      };
    }

    case GET_PERSONALISED_LOCATION_SUGGESTIONS_SUCCESS: {
      // Ignore personalised suggestions if input value isn't blank anymore
      if (state.whereField.trim() !== '') {
        return state;
      }

      const { recent = [], suggested = [] } = action.payload;

      return {
        ...state,
        whereSuggestions: [
          ...(!recent.length ? [] : [{
            sectionName: 'RECENT',
            suggestions: recent.map(suggestion => ({
              ...suggestion,
              analyticsType: 'recent'
            }))
          }]),
          ...(!suggested.length ? [] : [{
            sectionName: `${recent.length ? 'RELATED' : 'SUGGESTED'} AREA${suggested.length !== 1 ? 'S' : ''}`,
            suggestions: suggested.map(suggestion => ({
              ...suggestion,
              analyticsType: 'suggested'
            }))
          }])
        ]
      };
    }

    default: {
      return state;
    }
  }
}

export default composeReducers(reducer, {
  refinements: optimisticRefinementReducer
});

export const updateCriteria = ({ path, query }) => (dispatch, getState) => {
  const { query: lastQuery } = getState().search;
  const normalisedQuery = normaliseSearchQuery({ path, query });
  const tags = Object.keys(normalisedQuery)
    .map(paramName => `Search Criteria: ${paramName}`);

  dispatch({
    type: UPDATE_CRITERIA,
    payload: { lastQuery, query: normalisedQuery, locale: getState().location.locale },
    meta: { hotjar: tags }
  });
};

export const getCounts = ({ locale, path, query }) => (dispatch, getState) => {
  const { hasLoadedCounts, lastQuery } = getState().search;
  const normalisedQuery = normaliseSearchQuery({ path, query });
  const apiQuery = { ...normalisedQuery, sourcesystem: 'houston' };

  // First, let's see if we even need to get counts at all...
  if (hasLoadedCounts && !classificationCountsNeedUpdating(lastQuery, normalisedQuery)) {
    return Promise.resolve(); // Skip loading counts
  }

  dispatch({ type: GET_COUNTS_BEGIN, payload: { query: normalisedQuery, locale } });

  return api.jobs.counts(apiQuery, locale)
    .then(result => {
      dispatch({
        type: GET_COUNTS_SUCCESS,
        payload: { result, locale }
      });
    })
    .catch(Promise.CancellationError, () => { /* Swallow cancellations */ })
    .catch(err => {
      dispatch({ type: GET_COUNTS_ERROR, error: true, payload: { err, locale } });
    });
};

const setLastSearchWhere = val => localStore.set('lastSearchWhere', val);
const getLastSearchWhere = () => localStore.get('lastSearchWhere');

export const persistCriteriaLocally = () => (dispatch, getState) => {
  const state = getState();
  const { query } = state.search;
  const { locale } = state.location;

  const {
    whereId: whereid,
    locationId: location,
    areaId: area
  } = state.results.location || {};

  const cookieValue = encodeLastSearchCookie(new Date(), { ...query, whereid, location, area });
  const cookieDomain = locale.toLowerCase() === 'nz' ? 'seek.co.nz' : 'seek.com.au';
  const cookiePath = '/';

  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 1);
  const cookieExpires = expirationDate.toUTCString();

  document.cookie = `main=${cookieValue};domain=${cookieDomain};path=${cookiePath};expires=${cookieExpires}`;

  if (query.where) {
    setLastSearchWhere(query.where);
  }
};

export const persistCriteriaRemotely = (locale) => (dispatch, getState) => {
  const { user: { token } } = getState();
  return api.candidate.saveLastSearch({ locale, token })
    .catch(() => { /* Swallow errors */ });
};

export function didYouMeanClicked() {
  return {
    type: DID_YOU_MEAN_CLICKED
  };
}

export function quickSearchClassificationClicked() {
  return {
    type: QUICK_SEARCH_CLASSIFICATION_CLICKED,
    meta: { hotjar: 'Quick Search Classification Clicked' }
  };
}

export function quickSearchCityClicked() {
  return {
    type: QUICK_SEARCH_CITY_CLICKED,
    meta: { hotjar: 'Quick Search City Clicked' }
  };
}

export function quickSearchGraduateClicked() {
  return {
    type: QUICK_SEARCH_GRADUATE_CLICKED,
    meta: { hotjar: 'Quick Search Graduate Clicked' }
  };
}

export function sortModeChanged() {
  return {
    type: SORT_MODE_CHANGED
  };
}

export function jobClassificationClicked() {
  return {
    type: JOB_CLASSIFICATION_CLICKED,
    meta: { hotjar: 'Job Classification Clicked' }
  };
}

export function jobSubclassificationClicked() {
  return {
    type: JOB_SUBCLASSIFICATION_CLICKED,
    meta: { hotjar: 'Job Subclassification Clicked' }
  };
}

export function jobLocationClicked() {
  return {
    type: JOB_LOCATION_CLICKED,
    meta: { hotjar: 'Job Location Clicked' }
  };
}

export function jobAreaClicked() {
  return {
    type: JOB_AREA_CLICKED,
    meta: { hotjar: 'Job Area Clicked' }
  };
}

export function jobSuburbClicked() {
  return {
    type: JOB_SUBURB_CLICKED,
    meta: { hotjar: 'Job Suburb Clicked' }
  };
}

export function updateKeywordsField(keywordsField) {
  return {
    type: UPDATE_KEYWORDS_FIELD,
    payload: { keywordsField }
  };
}

export function whereFieldFocused() {
  return {
    type: WHERE_FIELD_FOCUSED
  };
}

export function whereFieldTyped() {
  return {
    type: WHERE_FIELD_TYPED
  };
}

export const updateWhereField = whereField => dispatch => {
  if (whereField === '') {
    dispatch({
      type: WHERE_FIELD_BLANKED
    });
  }

  dispatch({
    type: UPDATE_WHERE_FIELD,
    payload: { whereField }
  });
};

export function selectWhere() {
  return {
    type: SELECT_WHERE,
    meta: {
      metrics: {
        name: SELECT_WHERE
      }
    }
  };
}

export function updateActiveRefinement(refinement) {
  return {
    type: UPDATE_ACTIVE_REFINEMENT,
    payload: { refinement }
  };
}

export function clearActiveRefinement() {
  return {
    type: UPDATE_ACTIVE_REFINEMENT,
    payload: {
      refinement: null
    }
  };
}

export function selectRefinement(refinement) {
  return {
    type: SELECT_REFINEMENT,
    meta: {
      metrics: {
        name: SELECT_REFINEMENT,
        tags: { refinement }
      }
    }
  };
}

export function selectClassification() {
  return {
    type: SELECT_CLASSIFICATION,
    meta: {
      metrics: {
        name: SELECT_CLASSIFICATION
      }
    }
  };
}

export function searchSubmit() {
  return {
    type: SUBMIT_SEARCH,
    meta: {
      metrics: {
        name: SUBMIT_SEARCH
      }
    }
  };
}

function updateWhereSuggestions(whereSuggestions) {
  return {
    type: UPDATE_WHERE_SUGGESTIONS,
    payload: {
      whereSuggestions
    }
  };
}

function loadWhereSuggestions({ value, locale, cacheKey, dispatch, getState }) {
  api.locations.suggest({
    source: 'chalice',
    country: locale,
    q: value,
    correctDistance: 1,
    limit: 16
  }).then(whereSuggestions => {
    // Ignore fetched suggestions if input value has changed
    if (getState().search.whereField === value) {
      whereSuggestionsCache[cacheKey] = whereSuggestions;
      dispatch(updateWhereSuggestions(whereSuggestions));
    }
  });
}

const debouncedLoadWhereSuggestions = debounce(loadWhereSuggestions, 100);

export const fetchWhereSuggestions = ({ value, locale }) => (dispatch, getState) => {
  const cacheKey = value.toLowerCase().trim();
  const cachedSuggestions = whereSuggestionsCache[cacheKey];

  if (typeof cachedSuggestions !== 'undefined') {
    dispatch(updateWhereSuggestions(cachedSuggestions));
  } else {
    debouncedLoadWhereSuggestions({ value, locale, cacheKey, dispatch, getState });
  }
};

export const clearWhereSuggestions = () => dispatch => {
  dispatch(updateWhereSuggestions([]));
};

export const whereSuggestionSelected = ({ suggestion, suggestionValue }) => dispatch => {
  dispatch(updateWhereField(suggestionValue));

  switch (suggestion.analyticsType) {
    case 'recent': {
      return dispatch({
        type: WHERE_FIELD_RECENT_SELECTED,
        meta: { hotjar: 'Where Field Recent Selected' }
      });
    }

    case 'suggested': {
      return dispatch({
        type: WHERE_FIELD_SUGGESTED_SELECTED,
        meta: { hotjar: 'Where Field Suggested Selected' }
      });
    }

    default: {
      return dispatch({
        type: WHERE_FIELD_AUTOSUGGESTED_SELECTED,
        meta: { hotjar: 'Where Field Autosuggested Selected' }
      });
    }
  }
};

export const searchHelpClicked = () => ({
  type: SEARCH_HELP_CLICKED,
  meta: { hotjar: 'Search Help Clicked' }
});

const getPersonalisedLocationSuggestionsSuccess = ({ recent, suggested }) => dispatch => {
  dispatch({
    type: GET_PERSONALISED_LOCATION_SUGGESTIONS_SUCCESS,
    payload: { recent, suggested }
  });

  if (recent && recent.length) {
    dispatch({ type: WHERE_FIELD_RECENT_SHOWN });
  }

  if (suggested && suggested.length) {
    dispatch({ type: WHERE_FIELD_SUGGESTED_SHOWN });
  }
};

export const getPersonalisedLocationSuggestions = () => (dispatch, getState) => {
  const lastSearchWhereLocalStorage = getLastSearchWhere();
  const {
    location: { locale },
    lastSearch: { where: lastSearchWhereCookie }
  } = getState();

  const country = locale.toLowerCase();
  const recentLocation = lastSearchWhereLocalStorage || lastSearchWhereCookie;

  if (!personalisedSuggestionsCache[recentLocation]) {
    api.locations.personalisedSuggest({ country, recentLocation })
      .then(response => {
        personalisedSuggestionsCache[recentLocation] = response;
        dispatch(getPersonalisedLocationSuggestionsSuccess(response));
      });
  } else {
    const response = personalisedSuggestionsCache[recentLocation];
    dispatch(getPersonalisedLocationSuggestionsSuccess(response));
  }
};



/** WEBPACK FOOTER **
 ** ./app/store/search/search.js
 **/